define([
	"dojo/_base/declare",
	"underscore",
	"csi/constants",
	"csi/xml"
], function(declare, _, constants, xml) {

	var _mergeOptions = function(obj, params) {
		// summary: make sure requestParameters passed in are mixed with existing parameters
		if (params) {
			var p1 = obj.requestParameters;
			var p2 = params.requestParameters;
			if (p1 && p2) {
				delete params.requestParameters;
				obj.requestParameters = _.extend(_.clone(p1), p2);
			}

			obj = _.extend(obj, params);
		}

		return obj;
	};

	return declare(null, {
		// Boolean: tracks whether store is being filtered and/or sorted
		//bFiltered: false,
		//bSorted: false,

		// Boolean: true if typeNode parameter is just optimization rather than hard-coded in view - indicates to client whether or not nodes
		// will actually be filtered from stored list rather than use to optimize performance
		//bTargetType: false,

		// Chart: class to use to build chart store
		//chartClass: null,

		// String: indicates if query should include OR condition to allow for dividers of given type (should not be used for Names, Values, Extract(Values))
		//dividerType: false,

		// _Query: fixed query to apply to request before applying ad-hoc query
		//filterQuery: null,

		// _Query: query class to use to build binary query
		queryClass: null,

		// Store: store of Spec
		querySpecStore: null,

		// Query: sort query to apply to request if sort options are not specified (sort options will override sortQuery rather than be applied in addition)
		//sortQuery: null,

		// String: type of node being requested
		//typeNode: null,

		// Values: class to use to build values store
		//valuesClass: null,

		// ValuePairs: class to use to build store of value pairs
		//valuePairsClass: null,

		addFilterQuery: function(/*_Query*/q) {
			// summary: add additional query to filter request
			if (this.filterQuery) {
				this.filterQuery.importQuery(q);
			} else {
				this.filterQuery = q;
			}
		},

		createQuery: function() {
			// summary: return new instance of query
			return new this.queryClass();
		},

		getChartStore: function(/*Object*/storeOptions) {
			// summary: return store for chart with given options - chart should ignore dividers, sorting and dynamic queries
			console.assert(!!this.chartClass);
			console.assert(!!storeOptions, "Need to specify chart options");

			return new this.chartClass(_mergeOptions({
				filterQuery: this.filterQuery,
				requestParameters: this.requestParameters,
				sortQuery: this.sortQuery,
				typeNode: this.typeNode
			}, storeOptions));
		},

		getSpec: function(/*String*/id) {
			// summary: same as _QueryMixin - should this store have access to _QueryMixin rather than querySpecStore?
			var item = this.querySpecStore && this.querySpecStore.get(id);
			return item ? item.spec : null;
		},

		getValuesStore: function(/*Spec*/spec, /*Object?*/storeOptions) {
			// summary: return store for Spec with given id containing existing values - ignore dividers and sorting
			console.assert(!!this.valuesClass);

			if (!spec?.querySpec) {
				return null;
			}

			const {
				filterQuery,
				querySpecStore,
				requestModule,
				requestOperation,
				requestParameters,
				sortQuery,
				typeNode,
			} = this;

			const options = {
				filterQuery,
				querySpecStore,
				requestParameters,
				sortQuery,
				sourceStore: this,
				spec,
				typeNode,
			};

			if (this.hasOwnProperty("requestModule")) {
				options.requestModule = requestModule;
			}

			if (this.hasOwnProperty("requestOperation")) {
				options.requestOperation = requestOperation;
			}

			return new this.valuesClass(_mergeOptions(options, storeOptions));
		},

		getValuePairsStore: function(/*Spec*/spec1, /*Spec*/spec2, /*Object?*/storeOptions) {
			// summary: return store for Spec with given id containing existing values - ignore dividers and sorting
			console.assert(!!this.valuePairsClass);

			if (!spec1?.querySpec || !spec2?.querySpec) {
				return null;
			}

			const {
				filterQuery,
				querySpecStore,
				requestParameters,
				sortQuery,
				typeNode,
			} = this;

			return new this.valuePairsClass(_mergeOptions({
				filterQuery,
				querySpecStore,
				requestParameters,
				sortQuery,
				spec1,
				spec2,
				typeNode,
			}, storeOptions));
		},

		_getBinary: function(/*Object*/query, /*Object*/options, /*Boolean*/bReport) {
			// summary: apply base query before ad-hoc query and set type as DB optimization if known; do so only after query is populated
			// as it should only apply type if there are no other type filters.
			const usesDataStore = this._usesDataStore();
			let q = this.createQuery();

			if (this.filterQuery) {
				q.importQuery(this.filterQuery);
			}

			// apply ad-hoc query
			this._getFilterQueryFromQuery(query, q, options);

			// track if store was filtered or sorted
			if (!bReport) {
				this.bFiltered = !q.isEmpty();
				this.bSorted = false;
			}

			// set type as DB optimization if known; do so only after query is populated
			// as it should only apply type if there are no other type filters.
			if (this.typeNode && !usesDataStore) {
				q.setType(this.typeNode);
			}

			// do not query for dividers or sort when reporting
			if (!bReport) {
				// handle special case of dividers where we always want to show dividers regardless of filter
				// along with other nodes of type that match filters
				if (this.dividerType && !usesDataStore) {
					q = this._getDividerQuery(q);
				}

				// apply sort options - ignore if sort is set to function which may be used as client sorting function
				// (to fix case of Observable Nodes store not sorting correctly since store knows order rather than item)
				const sortQueryFromOptions = this._getSortQueryFromOptions(options);
				const sortQuery = sortQueryFromOptions?.size() ? sortQueryFromOptions : this.sortQuery;
				if (sortQuery && !sortQuery.isEmpty()) {
					q.importQuery(sortQuery);
					this.bSorted = true;
				}
			}

			return q;
		},

		_getClientCacheQuery: function(/*Object?*/query, /*Object?*/options) {
			// summary: return query to be used against cached results based on given query and options; in such a case, the filterQuery and sortQuery
			// have already been applied. This function only needs to apply ad-hoc query and sorting. If only filtering, then dividers will be ignored.
			var q = this.createQuery();

			// apply ad-hoc query
			if (query) {
				this._getFilterQueryFromQuery(query, q, options);
			}

			// since the type is normally only applied as an optimization for the DB, we can leave it out of the client query;
			// the divider type, if any, is needed though as they should never be filtered out
			if (this.dividerType) {
				q = this._getDividerQuery(q);
			}

			// apply ad-hoc sorting if not only filtering
			var sortQuery = this._getSortQueryFromOptions(options);
			if (sortQuery && !sortQuery.isEmpty()) {
				q.importQuery(sortQuery);
			}

			return q;
		},

		_getDividerQuery: function(/*Query*/q) {
			// summary: apply query filters for dividers
			var qRoot = this.createQuery();
			var qOr = qRoot.Or();
			qOr.Node(xml.PROP_TYPE, xml.OP_EQUALS, this.dividerType);
			qOr.And().importQuery(q);

			qRoot.bServerOnly = qRoot.bServerOnly || q.bServerOnly;
			qRoot.bDirect = qRoot.bDirect || q.bDirect;

			return qRoot;
		},

		_getFilterQueryFromQuery: function(/*Object*/query, /*Query?*/q, /*Object*/options,) {
			// summary: return query for ad-hoc query
			q = q || this.createQuery();

			if (this.queryByAny) {
				q = q.Or();
			}

			_.each(query, function(value, id) {
				var spec = this.getSpec(id);
				if (spec) {
					spec.applyAdHocFilter(q, value);
				} else {
					console.warn("Cannot apply filter for: " + id);
				}
			}, this);

			const quickSearchQuery = this._lastQuickSearchQuery = options?.quickSearchQuery;
			if (quickSearchQuery) {
				const slimQuery = q.Or();
				quickSearchQuery.forEach(item => slimQuery.Attr(item.key, xml.OP_MATCHES, item.value || "*"));
			}

			return q;
		},

		_getSortQueryFromOptions: function(/*Object*/options, /*Query?*/q) {
			// summary: return query that will sort based on the given options
			var sortSet = options && options.sort;
			if (!sortSet || _.isFunction(sortSet)) {
				return q;
			}

			q = q || this.createQuery();

			_.each(sortSet, function(sortObj) {
				var spec = this.getSpec(sortObj.attribute);
				if (spec) {
					spec.applySort(q, sortObj.descending);
				} else {
					console.warn("Cannot apply sort for: " + sortObj.attribute);
				}
			}, this);

			return q;
		},

		/**
		 * Return true if this store uses a data store query operation
		 * @return {boolean}
		 */
		_usesDataStore() {
			return this.requestModule === constants.MODULE_DATA_STORE;
		}
	});
});